import React from 'react';

const AdvancedGreeks = ({ currentLesson, setCurrentLesson, currentState, updateTutorialState, currentTutorialInfo, userStats, setUserStats, setTutorialComplete, tutorialComplete }) => {
  
  const moduleContent = {
    1: {
      title: "Delta Deep Dive",
      icon: "△",
      content: {
        keyPoints: [
          "Delta measures price sensitivity to underlying movement",
          "Call deltas: 0 to 1, Put deltas: -1 to 0",
          "At-the-money options have ~0.5 delta",
          "Delta changes as price and time change"
        ],
        example: "BTC call with 0.6 delta gains $60 for every $100 BTC price increase."
      },
      quiz: [
        {
          id: 'q1',
          question: "What does delta measure?",
          options: ["Time decay", "Price sensitivity", "Volatility impact", "Interest rate risk"],
          correct: 1,
          explanation: "Correct! Delta measures how much an option's price changes for a $1 move in the underlying."
        },
        {
          id: 'q2',
          question: "Call option deltas range from:",
          options: ["-1 to 0", "0 to 1", "-0.5 to 0.5", "0 to 100"],
          correct: 1,
          explanation: "Correct! Call deltas range from 0 (deep OTM) to 1 (deep ITM)."
        }
      ]
    },
    2: {
      title: "Gamma and Acceleration",
      icon: "Γ",
      content: {
        keyPoints: [
          "Gamma measures the rate of change of delta",
          "Highest gamma occurs at-the-money",
          "Gamma risk increases near expiration",
          "Long options have positive gamma"
        ],
        example: "Option with gamma of 0.05 sees delta increase by 0.05 for each $1 move in underlying."
      },
      quiz: [
        {
          id: 'q1',
          question: "What does gamma measure?",
          options: ["Delta changes", "Time decay", "Volatility sensitivity", "Interest rate impact"],
          correct: 0,
          explanation: "Correct! Gamma measures how much delta changes for a $1 move in the underlying price."
        },
        {
          id: 'q2',
          question: "When is gamma highest?",
          options: ["Deep in-the-money", "Deep out-of-the-money", "At-the-money", "All strikes equal"],
          correct: 2,
          explanation: "Correct! Gamma is highest for at-the-money options, especially near expiration."
        }
      ]
    },
    3: {
      title: "Theta and Time Decay",
      icon: "Θ",
      content: {
        keyPoints: [
          "Theta measures time decay of option value",
          "Always negative for long options",
          "Accelerates as expiration approaches",
          "Highest theta at-the-money near expiration"
        ],
        example: "Option with theta of -$25 loses $25 in value each day, all else equal."
      },
      quiz: [
        {
          id: 'q1',
          question: "What does theta represent?",
          options: ["Price sensitivity", "Time decay", "Volatility changes", "Interest rate impact"],
          correct: 1,
          explanation: "Correct! Theta measures how much option value decreases due to time passage."
        },
        {
          id: 'q2',
          question: "Theta is typically:",
          options: ["Positive for long options", "Negative for long options", "Zero for all options", "Always positive"],
          correct: 1,
          explanation: "Correct! Theta is negative for long options because time decay works against option holders."
        }
      ]
    },
    4: {
      title: "Vega and Volatility Risk",
      icon: "ν",
      content: {
        keyPoints: [
          "Vega measures sensitivity to volatility changes",
          "Higher vega for longer-term options",
          "At-the-money options have highest vega",
          "Vega decreases as expiration approaches"
        ],
        example: "Option with vega of 15 gains $15 for each 1% increase in implied volatility."
      },
      quiz: [
        {
          id: 'q1',
          question: "Vega measures sensitivity to:",
          options: ["Price changes", "Time decay", "Volatility changes", "Interest rates"],
          correct: 2,
          explanation: "Correct! Vega measures how much option price changes for a 1% change in implied volatility."
        },
        {
          id: 'q2',
          question: "Which options have highest vega?",
          options: ["Deep ITM", "Deep OTM", "At-the-money", "All equal"],
          correct: 2,
          explanation: "Correct! At-the-money options have the highest vega, especially with longer time to expiration."
        }
      ]
    },
    5: {
      title: "Rho and Interest Rates",
      icon: "ρ",
      content: {
        keyPoints: [
          "Rho measures sensitivity to interest rate changes",
          "More important for longer-term options",
          "Call rho positive, put rho negative",
          "Less significant in crypto markets"
        ],
        example: "Call option with rho of 25 gains $25 for each 1% increase in interest rates."
      },
      quiz: [
        {
          id: 'q1',
          question: "Rho measures sensitivity to:",
          options: ["Volatility", "Time decay", "Interest rates", "Price movements"],
          correct: 2,
          explanation: "Correct! Rho measures how option price changes with interest rate movements."
        },
        {
          id: 'q2',
          question: "Call options have:",
          options: ["Negative rho", "Positive rho", "Zero rho", "Variable rho"],
          correct: 1,
          explanation: "Correct! Call options have positive rho because higher rates increase call values."
        }
      ]
    },
    6: {
      title: "Second-Order Greeks",
      icon: "∂²",
      content: {
        keyPoints: [
          "Charm: delta decay over time",
          "Vanna: delta sensitivity to volatility",
          "Volga: vega sensitivity to volatility",
          "Important for sophisticated hedging"
        ],
        example: "Charm of -0.02 means delta decreases by 0.02 each day due to time decay."
      },
      quiz: [
        {
          id: 'q1',
          question: "Charm measures:",
          options: ["Delta decay over time", "Gamma changes", "Vega stability", "Theta acceleration"],
          correct: 0,
          explanation: "Correct! Charm measures how delta changes due to time decay."
        },
        {
          id: 'q2',
          question: "Vanna measures:",
          options: ["Vega changes to volatility", "Delta sensitivity to volatility", "Gamma acceleration", "Theta decay"],
          correct: 1,
          explanation: "Correct! Vanna measures how delta changes with volatility movements."
        }
      ]
    },
    7: {
      title: "Greeks in Portfolio Management",
      icon: "📊",
      content: {
        keyPoints: [
          "Net portfolio Greeks for risk assessment",
          "Greek-neutral strategies",
          "Hedging specific Greek exposures",
          "Dynamic rebalancing requirements"
        ],
        example: "Portfolio delta of +150 means gain $150 for every $1 underlying increase."
      },
      quiz: [
        {
          id: 'q1',
          question: "Portfolio Greeks represent:",
          options: ["Individual option risks", "Combined position risks", "Market volatility", "Time remaining"],
          correct: 1,
          explanation: "Correct! Portfolio Greeks show the combined risk exposures of all positions."
        },
        {
          id: 'q2',
          question: "Greek-neutral strategies aim to:",
          options: ["Maximize specific risks", "Eliminate specific risks", "Increase leverage", "Reduce costs"],
          correct: 1,
          explanation: "Correct! Greek-neutral strategies eliminate exposure to specific risk factors."
        }
      ]
    },
    8: {
      title: "Advanced Greek Applications",
      icon: "🎯",
      content: {
        keyPoints: [
          "Greeks for risk management and P&L attribution",
          "Scenario analysis using Greeks",
          "Greeks in automated trading systems",
          "Real-time Greek monitoring and alerts"
        ],
        example: "Automated system rebalances when portfolio delta exceeds ±100 threshold."
      },
      quiz: [
        {
          id: 'q1',
          question: "Greeks are used for:",
          options: ["Only option pricing", "Risk management and P&L analysis", "Market prediction", "Tax calculations"],
          correct: 1,
          explanation: "Correct! Greeks are essential for risk management, P&L attribution, and portfolio analysis."
        },
        {
          id: 'q2',
          question: "Real-time Greek monitoring helps:",
          options: ["Predict future prices", "Manage risk exposures", "Calculate taxes", "Set strike prices"],
          correct: 1,
          explanation: "Correct! Real-time monitoring helps traders manage their risk exposures effectively."
        }
      ]
    }
  };

  const currentModuleContent = moduleContent[currentLesson];
  if (!currentModuleContent) return null;

  if (currentState.phase === 'intro') {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <div className="text-6xl mb-4">{currentModuleContent.icon}</div>
          <h3 className="text-white text-2xl font-bold mb-2">Module {currentLesson}: {currentModuleContent.title}</h3>
          <p className="text-gray-400">Advanced Greeks and risk metrics</p>
        </div>

        <div className="bg-atticus-navy-800 rounded-lg p-6 space-y-4">
          <div className="border-l-4 border-blue-500 pl-4">
            <h4 className="text-white font-bold mb-2">🧮 Key Concepts</h4>
            <div className="space-y-2">
              {currentModuleContent.content.keyPoints.map((point, index) => (
                <p key={index} className="text-gray-300 text-sm">• {point}</p>
              ))}
            </div>
          </div>

          <div className="bg-cyan-500/20 border border-cyan-500/50 rounded-lg p-4">
            <h5 className="text-cyan-400 font-bold mb-2">📊 Mathematical Example</h5>
            <p className="text-white text-sm">{currentModuleContent.content.example}</p>
          </div>
        </div>

        <button
          onClick={() => updateTutorialState({ phase: 'quiz' })}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-bold transition-colors"
        >
          Test Greeks Knowledge →
        </button>
      </div>
    );
  }

  if (currentState.phase === 'quiz') {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <h3 className="text-white text-2xl font-bold mb-2">Knowledge Check: {currentModuleContent.title}</h3>
          <p className="text-gray-400">Master these advanced risk metrics</p>
        </div>

        {currentModuleContent.quiz.map((question, qIndex) => {
          const userAnswer = currentState.quizAnswers[question.id];
          const isAnswered = userAnswer !== undefined;
          const isCorrect = userAnswer === question.correct;

          return (
            <div key={question.id} className="bg-atticus-navy-800 rounded-lg p-6">
              <div className="mb-4">
                <h4 className="text-white font-bold mb-3">Question {qIndex + 1}: {question.question}</h4>
                <div className="space-y-2">
                  {question.options.map((option, oIndex) => (
                    <button
                      key={oIndex}
                      onClick={() => updateTutorialState({ 
                        quizAnswers: { ...currentState.quizAnswers, [question.id]: oIndex }
                      })}
                      disabled={isAnswered}
                      className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                        isAnswered
                          ? oIndex === question.correct 
                            ? 'bg-green-500/20 border-green-500 text-green-300'
                            : oIndex === userAnswer
                              ? 'bg-red-500/20 border-red-500 text-red-300'
                              : 'bg-gray-700 border-gray-600 text-gray-400'
                          : 'bg-atticus-navy-700 border-gray-600 text-gray-300 hover:border-blue-500 hover:text-white'
                      }`}
                    >
                      {option}
                      {isAnswered && oIndex === question.correct && <span className="float-right text-green-400">✓</span>}
                      {isAnswered && oIndex === userAnswer && oIndex !== question.correct && <span className="float-right text-red-400">✗</span>}
                    </button>
                  ))}
                </div>
              </div>

              {isAnswered && (
                <div className={`p-3 rounded-lg border ${isCorrect ? 'bg-green-500/20 border-green-500' : 'bg-red-500/20 border-red-500'}`}>
                  <p className={`text-sm ${isCorrect ? 'text-green-300' : 'text-red-300'}`}>
                    {isCorrect ? '✅ Correct! ' : '❌ Incorrect. '}{question.explanation}
                  </p>
                </div>
              )}
            </div>
          );
        })}

        {Object.keys(currentState.quizAnswers).length === currentModuleContent.quiz.length && (
          <div className="text-center">
            {currentModuleContent.quiz.every(q => currentState.quizAnswers[q.id] === q.correct) ? (
              <div className="space-y-4">
                <div className="bg-green-500/20 border border-green-500 rounded-lg p-4">
                  <div className="text-green-400 font-bold text-lg">🎉 Module {currentLesson} Complete!</div>
                  <div className="text-green-300">You've mastered {currentModuleContent.title}!</div>
                </div>
                <button
                  onClick={() => {
                    const moduleXP = currentTutorialInfo.xp_per_module;
                    setUserStats(prev => ({
                      ...prev,
                      completed_lessons: [...prev.completed_lessons, `advanced_greeks_module_${currentLesson}`],
                      total_xp: prev.total_xp + moduleXP,
                      badges: currentLesson === 1 ? [...prev.badges, 'Greeks Analyst'] : prev.badges
                    }));

                    if (currentLesson < currentTutorialInfo.modules) {
                      setCurrentLesson(currentLesson + 1);
                      updateTutorialState({ phase: 'intro', quizAnswers: {} });
                    } else {
                      setTutorialComplete(true);
                      setUserStats(prev => ({
                        ...prev,
                        completed_tutorials: [...prev.completed_tutorials, 'advanced_greeks'],
                        certification_progress: Math.min(prev.certification_progress + 15, 100),
                        badges: [...prev.badges, 'Advanced Greeks Master']
                      }));
                    }
                  }}
                  className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 rounded-lg font-bold transition-colors"
                >
                  {currentLesson < currentTutorialInfo.modules ? `Continue to Module ${currentLesson + 1} →` : 'Complete Tutorial →'}
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-orange-500/20 border border-orange-500 rounded-lg p-4">
                  <div className="text-orange-400 font-bold">📖 Review Required</div>
                  <div className="text-orange-300">Please review the material and try again.</div>
                </div>
                <button
                  onClick={() => updateTutorialState({ quizAnswers: {}, phase: 'intro' })}
                  className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded-lg font-bold transition-colors"
                >
                  Review Module {currentLesson}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return null;
};

export default AdvancedGreeks;
